/* ============================================================
   BNE STUDIO — Contracts (Midnight Blueprint)
   Recreated from the design handoff:
   "BNE Studio - Contracts.html". Renders inside NightLayout
   (shared nav/footer + motion engine + scoped design system).
   Placeholder CTAs point to "#" until they exist; cross-links
   route to their product pages. The page lives at /contracts.
   ============================================================ */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import NightLayout from "../components/night/NightLayout";

// arrow used by the primary CTAs
const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ContractsPage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "BNE Contracts — Put the paperwork behind the music in place";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <NightLayout description="Splits, agreements, and signatures — handled before the release goes live." active="contracts">
      {/* HERO */}
      <header className="phero" style={{ "--c": "var(--p-contracts)" }}>
        <div className="wrap">
          <div className="phero-grid">
            <div className="reveal">
              <div className="ico-badge">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M14 3v4a1 1 0 0 0 1 1h4M18 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8l6 6v3M8 18c1.4-2 2.8-2 4 0s2.6 2 4 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="eyebrow" style={{ marginBottom: "16px" }}>Release</span>
              <h1 className="grad-text">Splits and deals, done.</h1>
              <p className="lead">Splits, agreements, and signatures — handled before the release goes live.</p>
              <div className="ctas">
                <a className="btn btn-primary btn-lg btn-arrow" href="https://contracts.bne.studio">
                  Create a Contract <Arrow />
                </a>
                <span className="domain">contracts.bne.studio</span>
              </div>
            </div>
            <div className="reveal" data-d="1">
              <div className="big-mock">
                <div className="bm-bar">
                  <i />
                  <i />
                  <i />
                  <span className="t">contracts.bne.studio / neon-hours</span>
                  <span className="pill">Ready to sign</span>
                </div>
                <div className="bm-body">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                    <b style={{ fontFamily: "var(--disp)", fontSize: "17px", color: "var(--heading)", letterSpacing: "-0.02em" }}>Producer Agreement</b>
                    <span className="chip" style={{ color: "var(--p-contracts)" }}>Split sheet</span>
                  </div>
                  <div className="row">
                    <span style={{ fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: ".04em", textTransform: "uppercase", color: "var(--fog)", width: "84px" }}>Work</span>
                    <span className="m">
                      <b>Neon Hours</b>
                    </span>
                  </div>
                  <div className="row">
                    <span style={{ fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: ".04em", textTransform: "uppercase", color: "var(--fog)", width: "84px" }}>Parties</span>
                    <span className="m">
                      <b>Ava R. & Jordan M.</b>
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "8px", margin: "10px 0" }}>
                    <div style={{ flex: 1, borderRadius: "11px", padding: "13px", boxShadow: "var(--hair-soft)", textAlign: "center" }}>
                      <div style={{ fontFamily: "var(--disp)", fontSize: "24px", fontWeight: 700, color: "var(--p-contracts)" }}>60%</div>
                      <div style={{ fontFamily: "var(--mono)", fontSize: "9px", textTransform: "uppercase", color: "var(--fog)", marginTop: "3px" }}>Artist</div>
                    </div>
                    <div style={{ flex: 1, borderRadius: "11px", padding: "13px", boxShadow: "var(--hair-soft)", textAlign: "center" }}>
                      <div style={{ fontFamily: "var(--disp)", fontSize: "24px", fontWeight: 700, color: "var(--p-contracts)" }}>40%</div>
                      <div style={{ fontFamily: "var(--mono)", fontSize: "9px", textTransform: "uppercase", color: "var(--fog)", marginTop: "3px" }}>Producer</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <div style={{ flex: 1, borderRadius: "11px", padding: "14px", boxShadow: "inset 0 0 0 1px rgba(170,180,198,.34)", background: "rgba(170,180,198,.06)" }}>
                      <div className="serif" style={{ fontSize: "20px", color: "var(--heading)" }}>Ava Reyes</div>
                      <div style={{ fontFamily: "var(--mono)", fontSize: "8.5px", textTransform: "uppercase", color: "var(--fog)", marginTop: "8px" }}>Signed · Mar 6</div>
                    </div>
                    <div style={{ flex: 1, borderRadius: "11px", padding: "14px", boxShadow: "var(--hair-soft)", border: "1px dashed var(--line-mid)" }}>
                      <div className="serif" style={{ fontSize: "20px", color: "var(--faint)" }}>Awaiting</div>
                      <div style={{ fontFamily: "var(--mono)", fontSize: "8.5px", textTransform: "uppercase", color: "var(--fog)", marginTop: "8px" }}>Producer</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* OUTCOMES */}
      <section className="section tight" style={{ "--c": "var(--p-contracts)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">What it helps you do</span>
            <h2>The business behind the music, handled.</h2>
          </div>
          <div className="outcomes">
            <div className="outcome reveal" data-d="1">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 12h6M9 16h6M9 8h2M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Build from templates</h4>
              <p>Start from clear, music-specific templates instead of a blank page.</p>
            </div>
            <div className="outcome reveal" data-d="2">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M16 8a4 4 0 10-8 0M3 21v-2a5 5 0 015-5h8a5 5 0 015 5v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Document the terms</h4>
              <p>Capture splits, parties, and key terms so everyone agrees on the details.</p>
            </div>
            <div className="outcome reveal" data-d="3">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 12l2 2 4-4M12 3l7 4v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Keep it organized</h4>
              <p>Track status — draft, ready, signed — and keep every agreement in one place.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="section" style={{ "--c": "var(--p-contracts)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">How it works</span>
            <h2>From template to signed.</h2>
          </div>
          <div className="flow-chips reveal">
            <div className="fc">
              <div className="n">1</div>
              <div>
                <b>Pick a template</b>
                <span>Split sheet, producer, release</span>
              </div>
            </div>
            <div className="fc">
              <div className="n">2</div>
              <div>
                <b>Fill in the terms</b>
                <span>Parties, splits & details</span>
              </div>
            </div>
            <div className="fc">
              <div className="n">3</div>
              <div>
                <b>Send for signature</b>
                <span>Share & track who's signed</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="section tight" style={{ "--c": "var(--p-contracts)" }}>
        <div className="wrap">
          <div className="proof">
            <div className="pc reveal" data-d="1">
              <div className="lab">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 12h6M9 16h6M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Templates
              </div>
              <h4>Start from the right document.</h4>
              <p>Music-specific templates for the agreements that actually come up — no legal blank page, no guesswork.</p>
              <div style={{ marginTop: "4px" }}>
                <div className="row">
                  <span className="m">
                    <b>Split Sheet</b>
                    <span>writers & producers</span>
                  </span>
                  <span style={{ color: "var(--p-contracts)" }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                      <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>Producer Agreement</b>
                    <span>work-for-hire terms</span>
                  </span>
                  <span style={{ color: "var(--p-contracts)" }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                      <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>Release Form</b>
                    <span>features & samples</span>
                  </span>
                  <span style={{ color: "var(--p-contracts)" }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                      <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <div className="pc reveal" data-d="2">
              <div className="lab">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M3 17c3-4 6-4 9 0s6 4 9 0M4 13l4-9 4 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Signature & status
              </div>
              <h4>Know exactly where it stands.</h4>
              <p>A clear record of who's signed and what's outstanding, so paperwork never holds up the release.</p>
              <div style={{ marginTop: "4px" }}>
                <div className="row">
                  <span className="m">
                    <b>Ava Reyes</b>
                    <span>artist · 60%</span>
                  </span>
                  <span className="chip-status st-live">Signed</span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>Jordan Mills</b>
                    <span>producer · 40%</span>
                  </span>
                  <span className="chip-status st-q">Awaiting</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="section" style={{ "--c": "var(--p-contracts)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">Connected to the rest of BNE</span>
            <h2>Paperwork in step with the release.</h2>
          </div>
          <div className="eco-grid">
            <Link className="eco reveal" data-d="1" style={{ "--c": "var(--p-dist)" }} to="/distribute">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Distribution</b>
              <p>Settle splits before the release goes live.</p>
            </Link>
            <Link className="eco reveal" data-d="2" style={{ "--c": "var(--p-sync)" }} to="/sync">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 6a2 2 0 012-2h4l2 2.5h6a2 2 0 012 2V18a2 2 0 01-2 2H6a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Sync</b>
              <p>Pull the collaborators from a project.</p>
            </Link>
            <Link className="eco reveal" data-d="3" style={{ "--c": "var(--p-live)" }} to="/live">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 8a2 2 0 012-2h12a2 2 0 012 2 2 2 0 000 4 2 2 0 000 4 2 2 0 01-2 2H6a2 2 0 01-2-2 2 2 0 000-4 2 2 0 000-4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Live</b>
              <p>Paper the deal behind a show.</p>
            </Link>
            <Link className="eco reveal" data-d="4" style={{ "--c": "var(--p-vision)" }} to="/vision">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3ZM3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Vision</b>
              <p>Clear terms for directors & crew.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section tight">
        <div className="wrap">
          <div className="cta-band reveal-scale">
            <div className="glow" />
            <span className="eyebrow center" style={{ justifyContent: "center", marginBottom: "20px" }}>
              Release · Contracts
            </span>
            <h2 className="grad-text">Put it in writing.</h2>
            <p>Build the agreement, capture the splits, and keep the release moving.</p>
            <div className="row" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <a className="btn btn-primary btn-lg btn-arrow" href="https://contracts.bne.studio">
                Create a Contract <Arrow />
              </a>
              <Link className="btn btn-ghost btn-lg" to="/home">
                Explore the Ecosystem
              </Link>
            </div>
          </div>
        </div>
      </section>
    </NightLayout>
  );
};

export default ContractsPage;
